import React, { useEffect, useState } from 'react';
import './ApiKeysPage.scss';
import Loader from '../../../shared/components/common/loader/Loader';
import { getApiKeys, deleteApiKey } from './services/ApiKeysService';
import { ApiKeyResponse } from './models/ApiKeyResponse';
import { useAccount } from '../../../shared/contexts/account/AccountContexts';
import { DataTable } from '../../../shared/components/common/data-table/DataTable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { Tooltip } from 'primereact/tooltip';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import EmptyState from '../../../shared/components/common/empty-state/EmptyState';
import ColorTag from '../../../shared/components/color-tag/ColorTag';
import useToast from '../../../shared/hooks/global/UseToast';
import MoreBtn from '../../../shared/components/common/more-btn/MoreBtn';
import ApiKeyCreate from './components/create/ApiKeyCreate';
import ApiKeyEdit from './components/edit/ApiKeyEdit';
import ApiKeyExtend from './components/extend/ApiKeyExtend';
import { PROJECTS } from '../../../shared/routes/path';
import { useNavigate } from 'react-router-dom';
import usePermission from '../../../shared/hooks/global/UsePermission';
import useIsMobile from '../../../shared/hooks/global/UseIsMobile';
import { MOBILE_PAGINATOR_TEMPLATE } from '../../../shared/utils/mobile';

const ApiKeysPage: React.FC = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { accountResponse } = useAccount();
  // Brak `read` = zakładki nie ma w menu ORAZ wejście z URL nic nie daje.
  const apiKeysAccess = usePermission('API_KEYS');
  const [apiKeys, setApiKeys] = useState<ApiKeyResponse[] | null>(null);
  const [dialogMode, setDialogMode] = useState<{ type: 'create' | 'edit' | 'extend'; apiKey?: ApiKeyResponse } | null>(null);
  const { showToast, toastElement } = useToast();

  const navigate = useNavigate();

  const hideDialog = () => {
    setDialogMode(null);
    loadApiKeys();
  };

  useEffect(() => {
    loadApiKeys();
  }, []);

  const loadApiKeys = async () => {
    try {
      const data = await getApiKeys();
      setApiKeys(data);
    } catch (error) {
      console.error('Failed to load API keys:', error);
    }
  };

  const confirmDelete = (apiKey: ApiKeyResponse) => {
    confirmDialog({
      message: t('areYouSureDeleteApiKey'),
      header: t('areYouSure'),
      defaultFocus: 'accept',
      accept: () => handleDelete(apiKey.key),
    });
  };

  const handleDelete = async (key: string) => {
    try {
      const result = await deleteApiKey(key);
      if (result.success) {
        await loadApiKeys();
      }
    } catch (error) {
      console.error('Failed to delete API key:', error);
    }
  };

  const addNewApiKey = () => setDialogMode({ type: 'create' });

  const editApiKey = (apiKey: ApiKeyResponse) => setDialogMode({ type: 'edit', apiKey });

  const extendApiKey = (apiKey: ApiKeyResponse) => setDialogMode({ type: 'extend', apiKey });

  const getCreateApiKeyButton = () => {
    return (
      <Button className="btn btn--sm" onClick={() => addNewApiKey()}>
        <span className="ico--add ico--xs" />
        {t('createApiKey')}
      </Button>
    );
  };

  const canManage = apiKeysAccess.write;

  const emptyStateData = {
    title: canManage ? t('emptyStateApiKeys') : t('emptyStateApiKeysReadonly'),
    imageClassName: 'img--project',
    children: canManage ? getCreateApiKeyButton() : undefined,
  };

  if (!accountResponse || !accountResponse.addons.includes('API_KEYS') || !apiKeysAccess.read) {
    navigate(PROJECTS);
    return <></>;
  }

  const actionBodyTemplate = (apiKey: ApiKeyResponse) => {
    return (
      <div className="buttons-list">
        <Button
          className="action-btn api-keys-action-tip"
          onClick={() => editApiKey(apiKey)}
          data-pr-tooltip={t('edit')}
          data-pr-position="top"
        >
          <span className="ico--edit ico--xs" />
        </Button>
        <Button
          className="action-btn api-keys-action-tip"
          onClick={() => extendApiKey(apiKey)}
          data-pr-tooltip={t('extendValidity')}
          data-pr-position="top"
        >
          <span className="ico--calendar ico--xs" />
        </Button>
        <Button
          className="action-btn api-keys-action-tip"
          onClick={() => confirmDelete(apiKey)}
          data-pr-tooltip={t('delete')}
          data-pr-position="top"
        >
          <span className="ico--trash ico--xs" />
        </Button>
        <Tooltip target=".api-keys-action-tip" />
      </div>
    );
  };

  const validityBodyTemplate = (apiKey: ApiKeyResponse) => {
    if (!apiKey.expires) {
      return <span className="api-keys-page__validity">{t('neverExpires')}</span>;
    }
    const expiresDate = new Date(apiKey.expires);
    const daysUntilExpiry = Math.ceil((expiresDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    const expiringSoon = daysUntilExpiry <= 30;
    return (
      <span
        className={`api-keys-page__validity${expiringSoon ? ' api-keys-page__validity--soon' : ''}`}
      >
        {format(expiresDate, 'MMM d, yyyy')}
      </span>
    );
  };

  const copyToClipboard = async (text: string) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for insecure contexts where the Clipboard API is unavailable.
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      showToast(t('copied'), 'success');
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const keyBodyTemplate = (apiKey: ApiKeyResponse) => {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
          {apiKey.key.substring(0, 20)}...
        </span>
        <Button
          className="action-btn api-keys-copy-tip"
          onClick={() => copyToClipboard(apiKey.key)}
          data-pr-tooltip={t('copy')}
          data-pr-position="top"
        >
          <span className="ico--copy ico--xs" />
        </Button>
      </div>
    );
  };

  if (!apiKeys || !accountResponse) {
    return (
      <div className="api-keys-page">
        <Loader />
      </div>
    );
  }

  return (
    <div className="api-keys-page">
      <ConfirmDialog className="dialog-box" />
      <Tooltip target=".api-keys-copy-tip" />

      <Dialog
        visible={dialogMode !== null}
        onHide={() => setDialogMode(null)}
        header={dialogMode?.type === 'create' ? t('createApiKey') : dialogMode?.type === 'edit' ? t('editApiKey') : t('extendValidity')}
        className="dialog-box"
        modal
      >
        {dialogMode?.type === 'create' && <ApiKeyCreate afterSave={hideDialog} />}
        {dialogMode?.type === 'edit' && dialogMode.apiKey && <ApiKeyEdit apiKey={dialogMode.apiKey} afterSave={hideDialog} />}
        {dialogMode?.type === 'extend' && dialogMode.apiKey && <ApiKeyExtend apiKey={dialogMode.apiKey} afterSave={hideDialog} />}
      </Dialog>

      {apiKeys && apiKeys.length > 0 ? (
        <>
          {canManage && (
            <div className="api-keys-page__header">
              <div className="api-keys-page__add">{getCreateApiKeyButton()}</div>
            </div>
          )}

          <div className="api-keys-page__table">
            <DataTable
              stripedRows
              paginator
              paginatorClassName="pagination"
              removableSort
              sortField="created"
              sortOrder={-1}
              rows={50}
              rowsPerPageOptions={[10, 25, 50]}
              value={apiKeys}
              tableStyle={{ minWidth: '50rem' }}
              tableClassName="table"
              className="table-wrapper table-wrapper--stack api-keys-page__cards"
              responsiveLayout="stack"
              currentPageReportTemplate={t('rowsNumber') + ':'}
              paginatorTemplate={
                isMobile
                  ? MOBILE_PAGINATOR_TEMPLATE
                  : 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown'
              }
              pageLinkSize={isMobile ? 3 : 5}
            >
              <Column
                field="key"
                header={t('apiKey')}
                body={keyBodyTemplate}
                headerClassName="table__header"
                bodyClassName="table__cell--stack-title"
                style={{ minWidth: '200px' }}
              />
              <Column
                field="description"
                header={t('description')}
                body={(apiKey) => apiKey.description || '-'}
                headerClassName="table__header"
                bodyClassName="table__cell--stack-wide"
                style={{ minWidth: '150px' }}
              />
              <Column
                field="status"
                header={t('status')}
                body={(apiKey: ApiKeyResponse) => (
                  <ColorTag color={apiKey.status === 'ACTIVE' ? 'green' : 'red'}>
                    {apiKey.status === 'ACTIVE' ? t('active') : t('inactive')}
                  </ColorTag>
                )}
                headerClassName="table__header"
                style={{ minWidth: '100px' }}
              />
              <Column
                field="created"
                header={t('created')}
                sortable
                body={(apiKey) => format(new Date(apiKey.created), 'MMM d, yyyy')}
                headerClassName="table__header"
                style={{ minWidth: '120px' }}
              />
              <Column
                field="expires"
                header={t('validity')}
                sortable
                body={validityBodyTemplate}
                headerClassName="table__header"
                style={{ minWidth: '150px' }}
              />
              {canManage && (
                <Column
                  field="creatorEmail"
                  header={t('createdBy')}
                  body={(apiKey) => apiKey.creatorEmail || '-'}
                  headerClassName="table__header"
                  bodyClassName="table__cell--stack-wide"
                  style={{ minWidth: '150px' }}
                />
              )}
              {canManage && (
                <Column
                  header={t('action')}
                  body={(apiKey) => (
                    <MoreBtn children={actionBodyTemplate(apiKey)} rowId={apiKey.id} />
                  )}
                  headerClassName="table__header table__header--center"
                  bodyClassName="table__cell table__cell--center table__cell--stack-actions"
                  style={{ width: '50px', minWidth: '50px', maxWidth: '50px' }}
                />
              )}
            </DataTable>
          </div>
        </>
      ) : (
        <div className="empty-state-page">
          <EmptyState emptyStateData={emptyStateData} />
        </div>
      )}


      {toastElement}
    </div>
  );
};

export default ApiKeysPage;
